import { IMemberDepricated, IMinUserDepricated, IPNSProps, IUserDepricated } from "@mctypes/global.types";
import {
  Card,
  Checkbox as MantineCheckbox,
  ComboboxItem,
  Group,
  Select,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconCalendarWeek, IconInfoCircle } from "@tabler/icons-react";
import { ColDef } from "ag-grid-community";
import React, { useEffect, useMemo, useState } from "react";
import { McTitle } from "../../../components/";
import { McAutocomplete } from "../../../components/McAutocomplete";
import FoldableSection from "../../../components/FoldableSection/FoldableSection";
import McInputNew from "../../../components/McInput/McInputNew";
import McTextAreaNew from "../../../components/McInput/McTextAreaNew";
import { McMantineUserSelect } from "../../../components/McMantineUserSelect/McMantineUserSelect";
import { SimpleTagsMantine } from "../../../components/SimpleTags/SimpleTagsMantine";
import { UserCellRenderer } from "../../../components/ConnectedIssues/ConnectedIssues";
import ViewEditTable from "../../../components/ViewEditTable/ViewEditTable";
import { setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { ChecklistDisciplineRoles } from "@mcapps/shared/enums";
import { ToastMessagesEnums } from "@mcapps/shared/sharedConstants";
import ChecklistRemoveDepricated from "./admin/ChecklistRemove";
import ChecklistTypes from "./admin/ChecklistTypes";
import StampConfiguration from "./admin/StampConfiguration";
import { useGetUniqueChecklistTagsQuery } from "./checklistRTK";
import { updateChecklistThunk } from "./checklistSlice";
import { ChecklistStatusEnum, CHECKLISTSTATUSES, competenceNetworkList, TEMPLATESTATUSES } from "./constants";

const ChecklistAdminDepricated: React.FC = () => {
  const dispatch = useAppDispatch();
  const membersInRedux: IMemberDepricated[] = useAppSelector((state) => state.adminReducer.project.members);
  const project = useAppSelector((state) => state.adminReducer.project);
  const checklist = useAppSelector((state) => state.checklistReducer.checklist);
  const pnsItemsInRedux = useAppSelector((state) => state.adminReducer.project.pns);
  const [pnsItems, setPnsItems] =
    useState<{ comboLabel: string; _id: string; parentId: string; props: IPNSProps }[]>(null);

  const [removed, setRemoved] = useState<boolean>(false);

  const updateChecklist = async (prop: string, oldVal, newVal) => {
    dispatch(updateChecklistThunk(prop, oldVal, newVal));
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.SETTINGS_UPDATED));
  };

  useEffect(() => {
    setRemoved(checklist.status === ChecklistStatusEnum.REMOVED);
  }, [checklist.status]);

  useEffect(() => {
    setPnsItems(
      pnsItemsInRedux?.map((pns) => ({
        ...pns,
        comboLabel: [pns.props.pnsId, pns.props.title]
          .map((part) => part?.trim())
          .filter((part): part is string => part != null && part.length > 0)
          .join(" - "),
      })),
    );
  }, [pnsItemsInRedux]);

  const { data: tags = [] } = useGetUniqueChecklistTagsQuery({ projectId: project._id });

  const memberUsers: IMinUserDepricated[] = useMemo(
    () =>
      membersInRedux
        .map((member) => member.user)
        .filter((user): user is IMinUserDepricated => typeof user !== "string" && user != null),
    [membersInRedux],
  );

  const getUserId = (user?: string | IMinUserDepricated | null) => {
    if (user == null) return null;
    if (typeof user === "string") return user;
    return user._id ?? null;
  };

  const getUserName = (user?: string | IMinUserDepricated | null) => {
    if (user == null) return null;
    if (typeof user === "string") return user;
    return user.name;
  };

  const usersComboboxData: ComboboxItem[] = useMemo(
    () =>
      memberUsers.map((user) => ({
        value: user._id,
        label: user.name,
      })),
    [memberUsers],
  );

  const selectedPnsLabel = useMemo(
    () => pnsItems?.find((p) => p.props.pnsId === checklist.pns)?.comboLabel ?? "",
    [pnsItems, checklist.pns],
  );

  const pnsAutocompleteData: ComboboxItem[] = useMemo(
    () =>
      (pnsItems ?? []).map((p) => ({
        value: p._id,
        label: p.comboLabel,
      })),
    [pnsItems],
  );

  // Name must always be a string or crash in ag-grid cell editor when autocomplete is selecting empty/null user
  const emptyDocManager = { name: "", _id: "" } as IUserDepricated;
  const isNullOrEmpty = (value) => value == null || value === "";

  return (
    <Stack id="a" h="100%" w="100%" p="sm" gap="sm" style={{ overflow: "auto" }}>
      {checklist.isTemplate === true && (
        <ChecklistAdminSection title="Malinstillinger" storageKey="checklist-admin-template-settings">
          <DocumentManagers
            onSave={(roles) =>
              updateChecklist(
                "documentManagers",
                {
                  preparedBy: checklist.documentManagers?.preparedBy?.name ?? "",
                  checkedBy: checklist.documentManagers?.checkedBy?.name ?? "",
                  approvedBy: checklist.documentManagers?.approvedBy?.name ?? "",
                },
                {
                  preparedBy: isNullOrEmpty(roles.preparedBy?._id) === false ? roles.preparedBy : null,
                  checkedBy: isNullOrEmpty(roles.checkedBy?._id) === false ? roles.checkedBy : null,
                  approvedBy: isNullOrEmpty(roles.approvedBy?._id) === false ? roles.approvedBy : null,
                },
              )
            }
            documentManagers={{
              preparedBy: checklist.documentManagers?.preparedBy ?? emptyDocManager,
              checkedBy: checklist.documentManagers?.checkedBy ?? emptyDocManager,
              approvedBy: checklist.documentManagers?.approvedBy ?? emptyDocManager,
            }}
            projectMembers={[
              {
                user: emptyDocManager,
                isPassive: false,
                disciplin: "",
                group: null,
              },
              ...membersInRedux,
            ]}
          />
          <ChecklistField title="Admin">
            <McMantineUserSelect
              data={usersComboboxData}
              value={getUserId(checklist.admin)}
              onChange={(value) => updateChecklist("admin", getUserName(checklist.admin), value)}
            />
          </ChecklistField>
          <ChecklistField title="FLF">
            <McMantineUserSelect
              data={usersComboboxData}
              value={getUserId(checklist.disciplineLeader)}
              onChange={(value) => updateChecklist("disciplineLeader", getUserName(checklist.disciplineLeader), value)}
            />
          </ChecklistField>
          <ChecklistField title="Fagdisiplin">
            <Select
              clearable
              searchable
              placeholder="Velg fagdisiplin"
              data={ChecklistDisciplineRoles.map((d) => ({ value: d.code, label: d.title }))}
              value={checklist.disciplineCode ?? null}
              onChange={(value) => updateChecklist("disciplineCode", checklist.disciplineCode, value)}
            />
          </ChecklistField>
          <ChecklistField title="Delfagområde">
            <McInputNew
              type="text"
              onBlur={(e) => updateChecklist("subDiscipline", checklist.subDiscipline, e.target.value)}
              value={checklist.subDiscipline}
              disabled={removed === true}
            />
          </ChecklistField>
          <ChecklistField title="Dokumenttype">
            <McInputNew
              type="text"
              onBlur={(e) => updateChecklist("documentCategoryCode", checklist.documentCategoryCode, e.target.value)}
              value={checklist.documentCategoryCode ?? "SKL"}
              disabled={true}
            />
          </ChecklistField>
          <ChecklistField title="Dokumenttype 2">
            <McInputNew
              type="text"
              onBlur={(e) => updateChecklist("documentCategoryCode2", checklist.documentCategoryCode2, e.target.value)}
              value={checklist.documentCategoryCode2}
              disabled={removed === true}
            />
          </ChecklistField>
          <ChecklistField title="Kompetansenettverk">
            <Select
              clearable
              searchable
              placeholder="Velg kompetansenettverk"
              data={competenceNetworkList}
              value={checklist.competenceNetwork ?? null}
              onChange={(value) => updateChecklist("competenceNetwork", checklist.competenceNetwork, value)}
              disabled={removed === true}
            />
          </ChecklistField>
          <ChecklistField title="Revisionsnr">
            <McInputNew
              type="text"
              onBlur={(e) => updateChecklist("revisionNr", checklist.revisionNr, e.target.value)}
              value={checklist.revisionNr}
              disabled={removed === true}
            />
          </ChecklistField>
          <ChecklistField title="Revisionsdato">
            <DateInput
              styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
              clearable
              valueFormat="DD.MM.YYYY"
              value={checklist.revisionDate != null ? new Date(checklist.revisionDate) : null}
              onChange={(value) =>
                updateChecklist("revisionDate", checklist.revisionDate, value?.slice(0, 10) ?? null)
              }
              rightSection={
                checklist.revisionDate == null && <IconCalendarWeek color="var(--mantine-color-warmgray-3)" />
              }
              disabled={removed === true}
            />
          </ChecklistField>
          <ChecklistField title="Malbeskrivelse">
            <McTextAreaNew
              value={checklist.templateDescription ?? ""}
              onBlur={(e) => updateChecklist("templateDescription", checklist.templateDescription, e.target.value)}
              disabled={removed === true}
              autosize
            />
          </ChecklistField>
        </ChecklistAdminSection>
      )}

      <ChecklistAdminSection title="Innstillinger" storageKey="checklist-admin-settings">
        <ChecklistField title="Navn">
          <McInputNew
            required={true}
            onBlur={(e) => updateChecklist("name", checklist.name, e.target.value)}
            value={checklist.name}
            disabled={removed === true}
          />
        </ChecklistField>
        <ChecklistField title="Status">
          <Select
            allowDeselect={false}
            data={(checklist.isTemplate === true ? TEMPLATESTATUSES : CHECKLISTSTATUSES).filter(
              (x) => x.value !== "REMOVED",
            )}
            value={checklist.status}
            onChange={(newValue) => updateChecklist("status", checklist.status, newValue)}
            disabled={removed === true}
          />
        </ChecklistField>
        <ChecklistField title="Fra mal">
          <McInputNew disabled={true} value={checklist.generatedFromTemplate?.name ?? ""} />
        </ChecklistField>
        <ChecklistField title="Etiketter på sjekkliste">
          <SimpleTagsMantine
            editable={removed === false}
            activeTags={checklist.tags || []}
            tagOptions={tags.map((t) => t._id)}
            onChange={(tags) => updateChecklist("tags", checklist.tags, tags)}
          />
        </ChecklistField>
        <ChecklistField title="Etiketter på spørsmål">
          <Stack gap={6}>
            <MantineCheckbox
              checked={checklist.itemTagsActive === true}
              onChange={() =>
                updateChecklist(
                  "itemTagsActive",
                  checklist.itemTagsActive,
                  checklist.itemTagsActive === true ? false : true,
                )
              }
            />
            {checklist.itemTagsActive === true && (
              <SimpleTagsMantine
                editable={removed === false}
                activeTags={checklist.itemTags || []}
                tagOptions={checklist.itemTags || []}
                onChange={(itemTags) => {
                  const unique = [...new Set(itemTags)];
                  if (JSON.stringify(unique) !== JSON.stringify(checklist.itemTags)) {
                    updateChecklist("itemTags", checklist.itemTags, unique);
                  }
                }}
              />
            )}
          </Stack>
        </ChecklistField>
        <ChecklistField title="Aktiver IR på opprinnelige roller">
          <MantineCheckbox
            checked={checklist.includeIrOnDefaultRoles}
            onChange={() =>
              updateChecklist(
                "includeIrOnDefaultRoles",
                checklist.includeIrOnDefaultRoles,
                !checklist.includeIrOnDefaultRoles,
              )
            }
          />
        </ChecklistField>
        <ChecklistField title="Aktiver uavklart (UAV) på opprinnelige roller">
          <MantineCheckbox
            checked={checklist.includeUavOnDefaultRoles}
            onChange={() =>
              updateChecklist(
                "includeUavOnDefaultRoles",
                checklist.includeUavOnDefaultRoles,
                !checklist.includeUavOnDefaultRoles,
              )
            }
          />
        </ChecklistField>
        <ChecklistField title="Sjekklisteroller">
          <ChecklistTypes
            checkTypes={checklist.checkTypes}
            editable={removed === false}
            onChange={(types) => updateChecklist("checkTypes", JSON.stringify(checklist.checkTypes), types)}
          />
        </ChecklistField>
        {checklist.isTemplate === false && (
          <>
            <ChecklistField title="Ansvarlig">
              <McMantineUserSelect
                data={usersComboboxData}
                value={getUserId(checklist.assignedTo)}
                disabled={removed === true}
                onChange={(value) =>
                  updateChecklist(
                    "assignedTo",
                    getUserName(checklist.assignedTo),
                    value == null ? null : memberUsers.find((user) => user._id === value) ?? null,
                  )
                }
              />
            </ChecklistField>
            <ChecklistField title="Tidsfrist">
              <DateInput
                styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
                clearable
                valueFormat="DD.MM.YYYY"
                value={checklist.dueDate != null ? new Date(checklist.dueDate) : null}
                onChange={(value) => updateChecklist("dueDate", checklist.dueDate, value != null ? value : undefined)}
                rightSection={checklist.dueDate == null && <IconCalendarWeek color="var(--mantine-color-warmgray-3)" />}
                disabled={removed === true}
              />
            </ChecklistField>
            <ChecklistField title="PNS">
              {pnsItems != null && (
                <McAutocomplete
                  clearable
                  data={pnsAutocompleteData}
                  value={selectedPnsLabel}
                  onClear={() => updateChecklist("pns", checklist.pns, "")}
                  onCommit={(value) => {
                    const selectedPns = pnsItems.find((p) => p._id === value || p.comboLabel === value);
                    updateChecklist("pns", checklist.pns, selectedPns?.props?.pnsId ?? "");
                  }}
                />
              )}
            </ChecklistField>
          </>
        )}
      </ChecklistAdminSection>

      <ChecklistAdminSection title="Rapportinfo" storageKey="checklist-admin-report-settings">
        <ChecklistField title="Fase i oppdraget">
          <McInputNew
            onBlur={(e) => updateChecklist("phase", checklist.phase, e.target.value)}
            value={checklist.phase ?? ""}
            disabled={removed === true}
          />
        </ChecklistField>
        <ChecklistField title="Disiplin/fag">
          <McInputNew
            onBlur={(e) => updateChecklist("discipline", checklist.discipline, e.target.value)}
            value={checklist.discipline ?? ""}
            disabled={removed === true}
          />
        </ChecklistField>
        <ChecklistField title="Navn/nr. kontrollert dokument/aktivitet">
          <McInputNew
            onBlur={(e) => updateChecklist("activity", checklist.activity, e.target.value)}
            value={checklist.activity ?? ""}
            disabled={removed === true}
          />
        </ChecklistField>
        <ChecklistField title="Beskrivelse">
          <McTextAreaNew
            value={checklist.aboutText}
            onBlur={(e) => updateChecklist("aboutText", checklist.aboutText, e.target.value)}
            disabled={removed === true}
            autosize
          />
        </ChecklistField>
        <ChecklistField title="Rapporttekst">
          <StampConfiguration
            currentConfig={checklist.stampSettings}
            onChange={(newStampSettings) => updateChecklist("stampSettings", "", newStampSettings)}
          />
        </ChecklistField>
      </ChecklistAdminSection>

      <ChecklistRemoveDepricated disabled={removed === true} />
    </Stack>
  );
};

export default ChecklistAdminDepricated;

const ChecklistAdminSection: React.FC<{
  title: string;
  storageKey: string;
  children: React.ReactNode;
}> = ({ title, storageKey, children }) => {
  return (
    <FoldableSection storageKey={storageKey}>
      <FoldableSection.Header>
        <Title order={6}>{title}</Title>
      </FoldableSection.Header>
      <FoldableSection.Body>
        <Card w="100%" bg="#f8f9fa" bd="1px solid var(--mantine-color-warmgray-2)">
          <Stack gap="sm" maw="35rem">
            {children}
          </Stack>
        </Card>
      </FoldableSection.Body>
    </FoldableSection>
  );
};

const ChecklistField: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  return <McTitle title={title}>{children}</McTitle>;
};

export interface IDocumentManagers {
  preparedBy: IMinUserDepricated;
  checkedBy: IMinUserDepricated;
  approvedBy: IMinUserDepricated;
}

interface IDocumentManagerProps {
  documentManagers: IDocumentManagers;
  projectMembers: IMemberDepricated[];
  onSave: (data: IDocumentManagers) => void;
}

const DocumentManagers: React.FC<IDocumentManagerProps> = ({ onSave, documentManagers, projectMembers }) => {
  const saveChanges = (newData) => {
    onSave({
      preparedBy: newData[0].user,
      checkedBy: newData[1].user,
      approvedBy: newData[2].user,
    });
  };

  const _columns: ColDef[] = useMemo(() => {
    return [
      {
        headerName: "Rolle",
        field: "role",
        editable: false,
      },
      {
        headerName: "Bruker",
        field: "user",
        editable: true,
        cellRenderer: (params) => <UserCellRenderer height="15px" user={params.value} />,
        cellEditor: "agRichSelectCellEditor",
        valueGetter: (params) => {
          return params.data.user ?? null;
        },
        cellEditorParams: {
          cellRenderer: (params) => <UserCellRenderer height="15px" user={params.value} />,
          formatValue: (value) => value?.name ?? "",
          values: projectMembers.map((m) => m.user),
          allowTyping: true,
          filterList: true,
        },
      },
    ];
  }, [projectMembers]);

  const _data = useMemo(() => {
    return [
      {
        role: "Utarbeidet av",
        user: documentManagers?.preparedBy,
      },
      {
        role: "Kontrollert av",
        user: documentManagers?.checkedBy,
      },
      {
        role: "Godkjent av",
        user: documentManagers?.approvedBy,
      },
    ];
  }, [documentManagers]);

  return (
    <Stack gap="xs">
      <Group gap={6} align="center">
        <Text fw={500}>Dokumentansvarlige</Text>
        <Tooltip label='Legg til flere brukere under "Prosjektmedlemmer"' withArrow>
          <IconInfoCircle size={16} color="var(--mantine-color-blue-6)" />
        </Tooltip>
      </Group>
      <ViewEditTable
        gridOptions={{
          popupParent: document.body,
        }}
        data={_data}
        columns={_columns}
        onSave={saveChanges}
        newItemTemplateObject={{
          role: "",
          user: null,
        }}
        canEdit={true}
        canAddRows={false}
      />
    </Stack>
  );
};
